import { zodResolver } from '@hookform/resolvers/zod';
import { useId, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { isApiError } from '@/core/http';
import { Button, cn } from '@/core/ui';
import { ArrowRight, Check } from '@/core/ui/icons';
import { SignedUpButNotSignedIn, useSignUp } from '../application/session';
import {
  PASSWORD_MAX,
  PASSWORD_RULES,
  passwordViolations,
  signupSchema,
  type PasswordRule,
  type SignupValues,
} from '../domain/signup';
import { FieldError, FormAlert, InputShell, inputClass, RevealPasswordButton } from './AuthFields';

/** O que a tela faz quando o e-mail já tem conta (ou o login automático falhou). */
export interface ExistingAccountHandoff {
  email: string;
  /** Abrir "Esqueceu a senha?" já de cara. */
  forgot: boolean;
}

/**
 * "Criar conta": nome, e-mail e senha, com as regras da senha à vista.
 *
 * Dando certo, a pessoa já entra (a API cria a conta, o portal faz o login
 * com a mesma senha) e a rota /login a leva para onde ia. E-mail que já tem
 * conta não é beco sem saída: oferece entrar ou redefinir a senha.
 */
export function SignupForm({
  onExistingAccount,
}: {
  onExistingAccount: (handoff: ExistingAccountHandoff) => void;
}) {
  const signUp = useSignUp();
  const [showPassword, setShowPassword] = useState(false);
  const form = useForm<SignupValues>({
    resolver: zodResolver(signupSchema),
    defaultValues: { name: '', email: '', password: '' },
  });
  const { errors, isSubmitted } = form.formState;
  const [password, email] = useWatch({ control: form.control, name: ['password', 'email'] });
  const broken = new Set<PasswordRule>(passwordViolations(password ?? '', email ?? ''));
  const ids = {
    name: useId(),
    email: useId(),
    pass: useId(),
    rules: useId(),
    errName: useId(),
    errEmail: useId(),
  };

  const submit = form.handleSubmit((values) =>
    signUp.mutateAsync(values).catch((error: unknown) => {
      if (error instanceof SignedUpButNotSignedIn) {
        onExistingAccount({ email: error.email, forgot: false });
        return;
      }
      if (isApiError(error) && error.code === 'weak_password') form.setFocus('password');
    }),
  );

  const existing = isApiError(signUp.error) && signUp.error.code === 'email_already_registered';
  const typedEmail = (email ?? '').trim();

  return (
    <form onSubmit={submit} noValidate className="space-y-5">
      {signUp.isError && !(signUp.error instanceof SignedUpButNotSignedIn) && (
        <FormAlert>
          {existing ? (
            <>
              <p className="font-medium">Já existe uma conta com este e-mail.</p>
              <p className="mt-1 text-red-300/90 light:text-red-700/90">
                Entre com ela — se não lembra a senha, dá para criar uma nova.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button
                  size="sm"
                  className="rounded-full"
                  onClick={() => onExistingAccount({ email: typedEmail, forgot: false })}
                >
                  Entrar com este e-mail
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  className="rounded-full"
                  onClick={() => onExistingAccount({ email: typedEmail, forgot: true })}
                >
                  Esqueci a senha
                </Button>
              </div>
            </>
          ) : (
            signUpErrorMessage(signUp.error)
          )}
        </FormAlert>
      )}

      <div className="space-y-1.5">
        <label htmlFor={ids.name} className="block text-sm font-medium text-fg-muted">
          Nome completo
        </label>
        <InputShell invalid={!!errors.name}>
          <input
            id={ids.name}
            autoFocus
            autoComplete="name"
            autoCapitalize="words"
            placeholder="Nome e sobrenome"
            aria-invalid={!!errors.name || undefined}
            aria-describedby={errors.name ? ids.errName : undefined}
            className={inputClass}
            {...form.register('name')}
          />
        </InputShell>
        {errors.name && <FieldError id={ids.errName}>{errors.name.message}</FieldError>}
      </div>

      <div className="space-y-1.5">
        <label htmlFor={ids.email} className="block text-sm font-medium text-fg-muted">
          E-mail
        </label>
        <InputShell invalid={!!errors.email || existing}>
          <input
            id={ids.email}
            type="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="none"
            spellCheck={false}
            placeholder="voce@email.com"
            aria-invalid={!!errors.email || existing || undefined}
            aria-describedby={errors.email ? ids.errEmail : undefined}
            className={inputClass}
            {...form.register('email')}
          />
        </InputShell>
        {errors.email && <FieldError id={ids.errEmail}>{errors.email.message}</FieldError>}
      </div>

      <div className="space-y-1.5">
        <label htmlFor={ids.pass} className="block text-sm font-medium text-fg-muted">
          Senha
        </label>
        <InputShell invalid={!!errors.password}>
          <input
            id={ids.pass}
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            maxLength={PASSWORD_MAX}
            placeholder="Crie uma senha"
            aria-invalid={!!errors.password || undefined}
            aria-describedby={ids.rules}
            className={cn(inputClass, 'pr-11')}
            {...form.register('password')}
          />
          <RevealPasswordButton shown={showPassword} onToggle={() => setShowPassword((v) => !v)} />
        </InputShell>
        {/* As regras ficam sempre à vista e vão marcando conforme a pessoa digita. */}
        <ul id={ids.rules} aria-label="A senha precisa ter" className="grid gap-1 pt-1 sm:grid-cols-2">
          {PASSWORD_RULES.map(({ rule, label }) => {
            const ok = !!password && !broken.has(rule);
            const failed = isSubmitted && !ok;
            return (
              <li
                key={rule}
                className={cn(
                  'flex items-center gap-1.5 text-xs transition-colors',
                  ok ? 'text-brand-300' : failed ? 'text-red-400 light:text-red-600' : 'text-fg-subtle',
                )}
              >
                <span
                  aria-hidden
                  className={cn(
                    'flex size-4 shrink-0 items-center justify-center rounded-full transition-colors',
                    ok ? 'bg-brand-400/20' : 'bg-surface-3',
                  )}
                >
                  {ok && <Check className="size-2.5" />}
                </span>
                {label}
                <span className="sr-only">{ok ? ' — ok' : ' — pendente'}</span>
              </li>
            );
          })}
        </ul>
      </div>

      <Button
        type="submit"
        size="lg"
        className="group mt-2 h-[3.25rem] w-full rounded-2xl text-[0.95rem] font-semibold"
        loading={signUp.isPending}
      >
        Criar conta
        {!signUp.isPending && (
          <ArrowRight aria-hidden className="size-4 transition-transform group-hover:translate-x-0.5" />
        )}
      </Button>
    </form>
  );
}

/** Tradução dos erros do cadastro para a tela. */
function signUpErrorMessage(error: unknown): string {
  if (!isApiError(error)) return 'Não foi possível criar a conta. Tente novamente.';
  switch (error.code) {
    case 'weak_password':
      return 'A senha não atende aos requisitos. Confira a lista abaixo do campo.';
    case 'invalid_name':
      return 'Confira o nome: informe nome e sobrenome, sem símbolos.';
    case 'invalid_email':
      return 'Confira o e-mail digitado.';
    case 'registration_rejected':
      return 'Não foi possível criar a conta com esses dados. Confira e tente novamente.';
    case 'rate_limited':
      return 'Muitas tentativas de cadastro. Aguarde alguns minutos e tente de novo.';
    case 'registration_unavailable':
      return 'O cadastro está indisponível no momento. Tente novamente mais tarde.';
    case 'network_error':
      return 'Sem conexão com o servidor. Verifique sua internet e tente de novo.';
    default:
      return 'Não foi possível criar a conta. Tente novamente.';
  }
}
